import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { useAuth } from '~/routes/components/AuthProvider'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import Dialog from '~/components/Dialog'
import CustomInput from '~/components/CustomInput'
import ProfileSettingsService from '~/api/services/profileSettings'
import { getAbortOfferColumns } from './util'
import TwoFaComp from '~/components/TwoFaComp'
import ExcelExport from '~/components/ExcelExport'
import StockExChangeService from '~/api/services/stockExchange'
import { handleErrors } from '~/utils/helpers'

const defaultFilter = {
  page: 0,
  rows: 10
}

const getActions = () => [
  {
    popupMessage: i18n.t('Change offer amount'),
    type: 'icon',
    name: 'pencil alternate',
    size: 'large',
    actionType: 'edit',
    display: ['in_progress'],
    displayedTextKey: 'statusLabel',
    displayedText: i18n.t('No actions')
  },
  {
    popupMessage: i18n.t('Cancel offer amount'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'close',
    display: ['in_progress'],
    displayedText: i18n.t('No actions')
  }
]

const Results: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const actions = getActions()
  const [isBusy, setIsBusy] = useState(false)
  const [showData, setShowData] = useState(null)
  const [resultsData, setResultsData] = useState(null)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [dataFiltered, setDataFiltered] = useState(defaultFilter)
  const { systemData, systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const [resultsSettings, setResultSettings] = useState(null)
  const [editAmount, setEditAmount] = useState(null)
  const [cancelAmount, setCancelAmount] = useState(null)
  const [fileSource, setFileSource] = useState(null)
  const [amount, setAmount] = useState<string | number>(0)
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const tfaOfferEdit = systemData?.roleSettings?.tfaOffer === 1
  const tfaOfferAbort = systemData?.roleSettings?.tfaAbortAmount === 1
  const tfaType = systemData?.logged?.tfaType
  const abbortOffersColumnHeaders = getAbortOfferColumns(amountUnit, priceLabel)
  const [abbortOffersRows, setAbbortOffersRows] = useState([])
  const [tableColumnHeaders, setTableColumnHeaders] = useState([
    {
      name: t('Trading month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: t('Status'),
      key: 'statusLabel'
    },
    {
      name: t('BLE price (€/100kg) [original Price]'),
      key: 'price',
      type: 'ble-price-change',
      info: t('If the BLE price increases as part of the hedging transaction in favor of the suppliers, this will also be confirmed. The original price is shown in square brackets for clarity.')
    },
    {
      name: t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: t('Offer ID'),
      key: 'uniqueId'
    },
    {
      name: t('Offer date'),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm'
    },
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number-whole'
    },
    {
      name: t('Hedging amount (in {{amountUnit}})', { amountUnit }),
      key: 'confirmedAmount',
      type: 'number-whole'
    },
    {
      name: t('Confirmation date'),
      key: 'confirmationDate',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm'
    },
    {
      name: t('Total (in €)'),
      key: 'total',
      type: 'price-total'
    }
  ])

  const changeAmountHeaders = [
    {
      name: t('Trading month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number-whole'
    },
    {
      name: t('Minimum contingent (in {{amountUnit}})', { amountUnit }),
      key: 'minimumContingent',
      type: 'number-whole'
    },
    {
      name: t('Max of trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'maxTradingAmount',
      type: 'number-whole'
    },
    {
      name: t('New amount offer (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'input-confirm'
    }
  ]

  useEffect(() => {
    fetchResultsData()
  }, [])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
    }
  }, [dataFiltered])

  async function fetchFilteredData() {
    setIsBusy(true)
    try {
      const responseResults = await StockExChangeService.getPreviewOffers(id, { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
      const responseSettings = await StockExChangeService.farmerOfferAmountsSettings(id)
      if (responseSettings.data?.hiddenPrices === 1) {
        setTableColumnHeaders(tableColumnHeaders.filter(column => column.key !== 'total'))
      }
      setResultSettings(responseSettings.data)
      setResultsData(responseResults.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function fetchResultsData() {
    setIsBusy(true)
    try {
      const responseShow = await StockExChangeService.showTradingSlot(id)
      const responseResults = await StockExChangeService.getPreviewOffers(id)
      setShowData(responseShow.data)
      setResultsData(responseResults.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
      isLoading={isBusy}
    />
  }

  async function getChangeAmountData(row) {
    try {
      if (tfaOfferEdit && (tfaType === 'email' || tfaType === 'sms')) {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      }
      const response = await StockExChangeService.getChangeAmountData(id, row.id)
      setEditAmount({
        id: row.id,
        ...response.data
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleSendCode(row) {
    setCancelAmount(row)
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        setCancelAmount(null)
        handleErrors(error)
      }
      setIsBusy(false)
    }
    setIsBusy(false)
  }

  async function handleCloseAction(row) {
    setIsBusy(true)
    try {
      const response = await StockExChangeService.abortTradingSlotOfferData(id, row.id)
      setAbbortOffersRows(response.data)
      if (tfaOfferAbort) {
        handleSendCode(row)
        return
      }
      setCancelAmount(row)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'edit':
        getChangeAmountData(row)
        return
      case 'close':
        handleCloseAction(row)
        return
      default:
        return
    }
  }

  function renderResultsData() {
    const actionsFiltered = resultsSettings?.canChangeAmounts ? actions : actions.filter(action => action.actionType !== 'edit')
    return <TableComponent
      tableColumnHeaders={resultsSettings?.farmerOfferChange && showData?.status === 'active' ? [...tableColumnHeaders, { name: t('Actions') }] : tableColumnHeaders}
      actions={resultsSettings?.farmerOfferChange && showData?.status === 'active' ? actionsFiltered : []}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      rows={rows}
      total={filtered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
    />
  }

  function renderActions() {
    return <ExcelExport
      url={`stock-exchange/trading-slots/${id}/offers/export`}
      fileName={'trading_slot_results'}
    />
  }

  function renderInputConfirm(type, value) {
    switch (type) {
      case 'input-confirm':
        return <CustomInput
          value={amount}
          error={verifyInputField()}
          onChange={(value) => setAmount(value || '')}
        />
      default:
        return
    }
  }

  async function handleChangeOfferAmount(data?) {
    setIsBusy(false)
    try {
      await StockExChangeService.changeAmountData(id, editAmount.id, { amount: parseInt(amount.toString()), ...data })
      setEditAmount(null)
      setAmount('')
      notify(t('Trading amount has been successfully changed'), 'success')
      const responseResults = await StockExChangeService.getPreviewOffers(id, { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
      setResultsData(responseResults.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function verifyInputField() {
    const disableBellowMin = Boolean(resultsSettings?.minContingentOffer)
    const enableExcidMax = Boolean(resultsSettings?.exceedingMaxAllowed)
    return (disableBellowMin ? (parseInt(amount.toString()) < editAmount?.minimumContingent) : false) ||
      (enableExcidMax ? false : (parseInt(amount.toString()) > parseInt(editAmount?.maxTradingAmount)))
  }

  function renderEditAmountModal() {
    return <Dialog
      isDialogOpen={Boolean(editAmount)}
      handleClose={() => {
        setAmount('')
        setEditAmount(null)
        setTfaCode('')
        setCorrectTfaCode(false)
      }}
      htmlContent
      isLoading={isBusy}
      style={{ width: '60%' }}
      textContent={<>
        <TableComponent
          tableColumnHeaders={changeAmountHeaders}
          renderColumn={(type, value) => renderInputConfirm(type, value)}
          removePagination
          rows={[editAmount]}
          total={1}
        />
        <div>
          {tfaOfferEdit && <TwoFaComp
            onChangeTfaCode={(code) => setTfaCode(code)}
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />}
        </div>
      </>}
      handleSubmit={() => handleChangeOfferAmount(tfaOfferEdit ? { code: tfaCode } : null)}
      disableSubmit={tfaOfferEdit ? !(correctTfaCode && !verifyInputField()) : verifyInputField()}
      textSubmit={t('Change amount')}
      textClose={t('Abort')}
      textHeader={t('Change existing trading amount')}
      type={'dialog'}
    />
  }

  async function abortTradingSlotOffer(data?) {
    setIsBusy(true)
    try {
      await StockExChangeService.abortTradingSlotOffer(id, cancelAmount?.id, { ...data })
      setCancelAmount(null)
      const responseResults = await StockExChangeService.getPreviewOffers(id, { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })
      setResultsData(responseResults.data)
      notify(t('Farmer amount blocked succesfully'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderCancelAmountModal() {
    return <Dialog
      isDialogOpen={Boolean(cancelAmount)}
      handleClose={() => {
        setCancelAmount(null)
        setTfaCode('')
        setCorrectTfaCode(false)
      }}
      disableSubmit={tfaOfferAbort ? !correctTfaCode : false}
      textSubmit={t('Confirm')}
      textClose={t('Abort')}
      textHeader={t('Delete')}
      textContent={<>
        <TableComponent
          tableColumnHeaders={abbortOffersColumnHeaders}
          rows={abbortOffersRows}
          total={abbortOffersRows.length}
          removePagination
        />
        <div>
          {tfaOfferAbort && <TwoFaComp
            onChangeTfaCode={(code) => setTfaCode(code)}
            onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
          />}
        </div>
      </>
      }
      handleSubmit={() => abortTradingSlotOffer(tfaOfferAbort ? { code: tfaCode } : null)}
    />
  }

  const { filtered, rows } = resultsData ? resultsData : { filtered: 0, rows: [] }
  const uniqueId = showData?.uniqueId || ''

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Trading slot ID: {{uniqueId}}', { uniqueId })}
    close={RouteTypes.STOCK_EXCHANGE}
    actions={renderActions()}
  >
    {showData && renderModalPreviewFile()}
    {editAmount && renderEditAmountModal()}
    {cancelAmount && renderCancelAmountModal()}
    {resultsData && resultsSettings && renderResultsData()}
  </Layout>
}
export default Results
